import {
	FileExtension,
	FileType,
	IndemnityAttachmentTypeLabelMap,
	IndemnityStatus,
	SuretyAccountId,
	SuretyIndemnityAgreementId,
} from "@inrev/common";
import {
	useAdminApproveAgentUploadIndemnityAgreement,
	useAdminUploadCompletedIndemnityAgreement,
} from "@portal/api/account/admin";
import { useDeleteIndemnityAgreementFile } from "@portal/api/account/agent";
import { AdminIndemnityAgreementAttachmentCard } from "@portal/components/indemnity-agreement/admin/AdminIndemnityAgreementAttachmentCard";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Button } from "@portal/components/primitives";
import { LocalFileUpload } from "@portal/components/ui/LocalFileUpload";
import { FC, useState } from "react";
import { LocallyUploadedFile } from "../../../../../types";

export type AdminIndemnityAgreementFileUploadProps = {
	suretyAccountId: SuretyAccountId;
	indemnityAgreementId: SuretyIndemnityAgreementId;
	indemnityAgreementStatus: IndemnityStatus;
};

export const AdminAccountIndemnityAgreementFileUpload: FC<
	AdminIndemnityAgreementFileUploadProps
> = ({ suretyAccountId, indemnityAgreementId, indemnityAgreementStatus }) => {
	const { approveAgentUploadIndemnityAgreement } = useAdminApproveAgentUploadIndemnityAgreement(
		suretyAccountId,
		indemnityAgreementId,
	);

	const attachmentCard = (
		<AdminIndemnityAgreementAttachmentCard
			indemnityAgreementId={indemnityAgreementId}
			suretyAccountId={suretyAccountId}
			status={indemnityAgreementStatus}
		/>
	);

	if (indemnityAgreementStatus === "review") {
		return (
			<div className="w-full flex flex-col gap-y-4">
				{attachmentCard}
				<ConfirmationModalClickWrapper
					message="Are you sure?"
					subMessage="The system will issue bonds for any request in this account that is only pending an indemnity agreement."
					onConfirm={approveAgentUploadIndemnityAgreement}
				>
					<Button color="primary" className="w-fit self-end">
						Approve
					</Button>
				</ConfirmationModalClickWrapper>
			</div>
		);
	}

	return (
		<>
			{indemnityAgreementStatus === "complete" && attachmentCard}
			<_UploadComponent
				suretyAccountId={suretyAccountId}
				indemnityAgreementId={indemnityAgreementId}
				confirmationModalText={
					indemnityAgreementStatus === "complete"
						? "This will replace the existing indemnity agreement, the previous agreement will no longer be valid."
						: "The system will issue bonds for any request in this account that is only pending an indemnity agreement."
				}
			/>
		</>
	);
};

type UploadComponentProps = {
	suretyAccountId: SuretyAccountId;
	indemnityAgreementId: SuretyIndemnityAgreementId;
	confirmationModalText: string;
};

const _UploadComponent: FC<UploadComponentProps> = ({
	suretyAccountId,
	indemnityAgreementId,
	confirmationModalText,
}) => {
	const [indemnityAgreementFileUpload, setIndemnityAgreementFileUpload] = useState<
		LocallyUploadedFile[]
	>([]);

	const { deleteIndemnityAgreementFileIsLoading } = useDeleteIndemnityAgreementFile(
		suretyAccountId,
		indemnityAgreementId,
	);

	const { approveAgentUploadIndemnityAgreementIsLoading } =
		useAdminApproveAgentUploadIndemnityAgreement(suretyAccountId, indemnityAgreementId);

	const { uploadCompletedIndemnityAgreement, uploadCompletedIndemnityAgreementIsLoading } =
		useAdminUploadCompletedIndemnityAgreement(suretyAccountId, indemnityAgreementId);

	const handleUploadCompletedAgreement = () => {
		uploadCompletedIndemnityAgreement({
			fileId: indemnityAgreementFileUpload[0].id,
			indemnityAgreementId: indemnityAgreementId,
		});
		setIndemnityAgreementFileUpload([]);
	};

	return (
		<>
			<LocalFileUpload
				value={indemnityAgreementFileUpload}
				allowedTypesAndLabels={{
					[FileType.indemnity_agreement]:
						IndemnityAttachmentTypeLabelMap[FileType.indemnity_agreement],
				}}
				maxFiles={1}
				allowDelete={true}
				allowedExtensions={[FileExtension.pdf]}
				onChange={setIndemnityAgreementFileUpload}
				analyticsContext="indemnity"
			/>
			<ConfirmationModalClickWrapper
				message="Are you sure?"
				subMessage={confirmationModalText}
				shouldConfirm={!!indemnityAgreementFileUpload.length}
				onConfirm={handleUploadCompletedAgreement}
			>
				<Button
					color="primary"
					className="w-fit self-end"
					disabled={!indemnityAgreementFileUpload.length}
				>
					Upload
				</Button>
			</ConfirmationModalClickWrapper>
			{uploadCompletedIndemnityAgreementIsLoading ||
			deleteIndemnityAgreementFileIsLoading ||
			approveAgentUploadIndemnityAgreementIsLoading ? (
				<LoadingModal />
			) : null}
		</>
	);
};
